import { useRef, useState } from 'react';
import { keepPreviousData, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { MOMENT_MISMATCH, paybackApi, } from './api.js';
/**
 * Запросы раздела. Отчёт тяжёлый (до минуты), поэтому: свежесть 5 мин,
 * прошлые данные остаются на экране, пока грузятся новые, повтор — один и
 * только при сбое сети/5xx (не при «не подключено» и не при 4xx).
 */
export const PAYBACK_STALE_MS = 5 * 60_000;
/**
 * Один повтор — только при сбое сети и 500/502. Не повторяем: 4xx; 503 (не
 * подключено, очередь занята — повтор только удлинит очередь) и 504 (запрос
 * уже ждал до тайм-аута — второй такой же снова упрётся в 60 с nginx);
 * `contract_mismatch` (тот же ответ придёт снова).
 */
export function retryOnce(failureCount, err) {
    if (failureCount >= 1)
        return false;
    if (!(err instanceof ModuleRequestError))
        return true;
    if (err.code === 'contract_mismatch')
        return false;
    return err.status === 500 || err.status === 502;
}
const POLICY = {
    staleTime: PAYBACK_STALE_MS,
    placeholderData: keepPreviousData,
    retry: retryOnce,
    refetchOnWindowFocus: false,
};
/**
 * Запросы ОДНОЙ кампании: без `keepPreviousData`. Панель листается ↑↓ —
 * прошлые данные принадлежали бы другой кампании, поэтому при смене
 * `campaignId` блок показывает скелетон, а не чужие числа.
 */
const CAMPAIGN_POLICY = {
    staleTime: PAYBACK_STALE_MS,
    retry: retryOnce,
    refetchOnWindowFocus: false,
};
// --- ключи ------------------------------------------------------------------
export const PAYBACK_KEY = ['payback'];
function periodKey(q) {
    return { from: q.from, to: q.to, accounts: [...q.accounts].sort().join(',') };
}
export const reportKey = (q) => [
    'payback',
    'report',
    {
        ...periodKey(q),
        moment: q.moment,
        model: q.model ?? null,
        includeDays: !!q.includeDays,
        // без разделения ключ тот же, что раньше
        ...(q.split ? { split: q.split } : {}),
    },
];
export const campaignMarkersKey = (q) => [
    'payback',
    'campaign-markers',
    {
        ...periodKey(q),
        campaignId: q.campaignId,
        moment: q.moment,
        model: q.model ?? null,
        ...(q.revision !== undefined ? { revision: q.revision } : {}),
    },
];
export const campaignDaysKey = (q) => [
    'payback',
    'campaign-days',
    { ...periodKey(q), campaignId: q.campaignId, model: q.model ?? null },
];
export const campaignOutcomesKey = (q) => ['payback', 'campaign-outcomes', { ...periodKey(q), campaignId: q.campaignId }];
export const FRESHNESS_KEY = ['payback', 'freshness'];
export const ACCOUNTS_KEY = ['payback', 'accounts'];
export const HEALTH_KEY = ['payback', 'health'];
export function useReport(q, opts = {}) {
    return useQuery({
        ...POLICY,
        queryKey: reportKey(q),
        queryFn: ({ signal }) => paybackApi.report(q, { signal }),
        enabled: opts.enabled ?? true,
    });
}
export function useCampaignMarkers(q, opts = {}) {
    return useQuery({
        ...CAMPAIGN_POLICY,
        queryKey: campaignMarkersKey(q),
        queryFn: ({ signal }) => paybackApi.campaignMarkers(q, { signal }),
        enabled: opts.enabled ?? true,
    });
}
export function useCampaignDays(q, opts = {}) {
    return useQuery({
        ...CAMPAIGN_POLICY,
        queryKey: campaignDaysKey(q),
        queryFn: ({ signal }) => paybackApi.campaignDays(q, { signal }),
        enabled: opts.enabled ?? true,
    });
}
export function useCampaignOutcomes(q, opts = {}) {
    return useQuery({
        ...CAMPAIGN_POLICY,
        queryKey: campaignOutcomesKey(q),
        queryFn: ({ signal }) => paybackApi.campaignOutcomes(q, { signal }),
        enabled: opts.enabled ?? true,
    });
}
/** Свежесть данных; без agent-data (503) — просто ошибка, экраны работают без неё. */
export function useFreshness() {
    return useQuery({
        ...POLICY,
        queryKey: FRESHNESS_KEY,
        queryFn: () => paybackApi.freshness(),
        retry: false,
    });
}
/** Кабинеты (названия, `adAccountId`) — справочник. */
export function useAccounts() {
    return useQuery({
        ...POLICY,
        queryKey: ACCOUNTS_KEY,
        queryFn: () => paybackApi.accounts(),
    });
}
export function useHealth() {
    return useQuery({
        queryKey: HEALTH_KEY,
        queryFn: () => paybackApi.health(),
        staleTime: PAYBACK_STALE_MS,
        retry: retryOnce,
        refetchOnWindowFocus: false,
    });
}
export const PAYBACK_MOMENTS = ['t0', 't24', 'd14'];
/**
 * Ответ этого момента настоящий: не заглушка прошлого ключа (`keepPreviousData`
 * — ключ включает период, кабинеты и модель) и того же момента. Даты из
 * `meta` не сверяем: auto-ads вправе нормализовать окно.
 */
export function isCurrentMomentReport(moment, r) {
    return !!r.data && !r.isPlaceholderData && r.data.moment === moment;
}
/**
 * Пришёл настоящий ответ этого ключа (не заглушка прошлого), но про другой
 * момент: это не «ещё грузится» (данные уже здесь и сами не сменятся), а
 * неожиданный ответ сервиса — ошибка с «Повторить».
 */
export function isMomentMismatch(moment, r) {
    return !!r.data && !r.isPlaceholderData && r.data.moment !== moment;
}
/**
 * Три отчёта «сразу при покупке / через сутки / на 14-й день» того же периода
 * (блок «Как уточнялся прогноз», таблица моментов в «О прогнозе» и в панели
 * кампании). Ленивые: `enabled`. `includeDays` — с дневной разбивкой (шаг
 * «Дни»): это другой ключ кэша, чем у недель и месяцев (`reportKey`).
 *
 * ПО ОДНОМУ: admin-api пускает к auto-ads не больше трёх тяжёлых запросов
 * сразу (на всех пользователей), поэтому следующий отчёт уходит только после
 * ответа на предыдущий — сверх основного отчёта экрана в полёте не больше
 * одного. «Ответ» — и успех, и ошибка: очередь не встаёт из-за одного сбоя.
 *
 * Упавший отчёт сам не повторяется НИКОГДА: ни при повторном `enabled`, ни
 * при новом наблюдателе (`retryOnMount: false`), — `enabled` у него
 * выключается, а вернуть его можно только `refetch()` (кнопка «Повторить»).
 */
export function useMomentReports(q, opts = {}) {
    const client = useQueryClient();
    const base = opts.enabled ?? true;
    const keys = PAYBACK_MOMENTS.map((moment) => reportKey({ ...q, moment }));
    // Очередь читаем из кэша (а не из результатов этого же рендера): «предыдущий
    // ответил» — успех или ошибка, и сейчас ничего не грузит. Возраст данных НЕ
    // проверяем: t0 может быть той же записью кэша, что и отчёт экрана, и его
    // никто не перезапрашивает — с проверкой «моложе 5 минут» звено после
    // долгого t24 не стартовало бы никогда (вечный скелетон d14).
    const settled = (i) => {
        const st = client.getQueryState(keys[i]);
        if (!st || st.fetchStatus !== 'idle')
            return false;
        return st.status === 'success' || st.status === 'error';
    };
    const failed = (i) => client.getQueryState(keys[i])?.status === 'error';
    const enabled = PAYBACK_MOMENTS.map((_, i) => base && !failed(i) && Array.from({ length: i }, (_, j) => j).every(settled));
    const results = useQueries({
        queries: PAYBACK_MOMENTS.map((moment, i) => {
            const rq = { ...q, moment };
            return {
                ...POLICY,
                queryKey: keys[i],
                queryFn: ({ signal }) => paybackApi.report(rq, { signal }),
                enabled: enabled[i],
                retryOnMount: false,
            };
        }),
    });
    return results.map((r, i) => {
        const moment = PAYBACK_MOMENTS[i];
        const mismatch = isMomentMismatch(moment, r);
        return {
            moment,
            data: r.data,
            isLoading: r.isPending,
            isFetching: r.isFetching,
            isError: r.isError || mismatch,
            error: r.isError ? r.error : mismatch ? new ModuleRequestError(MOMENT_MISMATCH, 200) : null,
            isPlaceholderData: r.isPlaceholderData,
            isCurrent: isCurrentMomentReport(moment, r),
            dataUpdatedAt: r.dataUpdatedAt,
            refetch: async () => {
                await r.refetch();
            },
        };
    });
}
/**
 * «Повторить» для `useMomentReports` — общий у блока «Как уточнялся прогноз»,
 * «О прогнозе» и панели кампании (кэш у них общий: 503 в одном месте видно во
 * всех). Только упавшие и ПО ОДНОМУ — как и очередь: admin-api пускает к
 * auto-ads не больше трёх тяжёлых запросов на всех, а основной отчёт экрана
 * может занимать один из них; параллельный повтор двух-трёх отчётов сам
 * получил бы 503 «очередь занята».
 *
 * Не параллельно очереди: пока любой из трёх отчётов `isFetching`, `busy`.
 * Защита от двойного клика — в обработчике (ref), а не только `disabled`:
 * второй клик того же кадра ещё видит старый рендер.
 */
export function useMomentsRetry(reports) {
    const running = useRef(false);
    const [isRunning, setRunning] = useState(false);
    const fetching = reports.some((r) => r.isFetching);
    const retry = () => {
        if (running.current || fetching)
            return;
        const failed = reports.filter((r) => r.isError);
        if (failed.length === 0)
            return;
        running.current = true;
        setRunning(true);
        void (async () => {
            try {
                for (const r of failed)
                    await r.refetch();
            }
            finally {
                running.current = false;
                setRunning(false);
            }
        })();
    };
    return { retry, busy: isRunning || fetching };
}
//# sourceMappingURL=queries.js.map